import { FC, useCallback, useEffect, useId, useRef, useState } from 'react'

import styled from 'styled-components'

import ErrorIcon from 'src/Assets/Svg/error-round.svg?react'
import { variables } from 'src/Styled/variables'

import { AudioFileDropzone, AudioFileDropzoneState } from './AudioFileDropzone'
import { AudioFileUploadTrackItem } from './AudioFileUploadTrackItem'
import {
  AUDIO_FILE_ERROR_MESSAGES,
  AudioFileErrorCode,
  validateAudioFile,
} from './validation'

export type AudioFileUploadStatus = 'idle' | 'uploading' | 'uploaded'

export type AudioFileUploadFileInfo = {
  name: string
  size?: number
}

export type AudioFileUploadProps = {
  file?: AudioFileUploadFileInfo | null
  status?: AudioFileUploadStatus
  progress?: number
  uploadedBytes?: number
  totalBytes?: number
  secondsLeft?: number | null
  processing?: boolean
  error?: string | null
  disabled?: boolean
  className?: string
  onFileSelect: (file: File) => void
  onValidationError?: (code: AudioFileErrorCode, file: File) => void
  onRemove?: () => void
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`

const DropzoneWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
`

const ErrorMessage = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: ${({ theme }) => theme.color.mediumRed};
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize14};
  font-weight: ${variables.fontWeight400};
  line-height: ${variables.lineHeight150per};
  word-break: break-word;
`

const ErrorIconWrapper = styled.div`
  display: flex;
  flex-shrink: 0;
  padding-top: 4px;
`

const TrackWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

const KeepPageOpenNote = styled.div`
  color: ${({ theme }) => theme.color.slateGrey};
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize14};
  font-weight: ${variables.fontWeight400};
  line-height: ${variables.lineHeight150per};
`

export const KEEP_PAGE_OPEN_MESSAGE =
  'Keep this page open until the upload finishes.'

export const AudioFileUpload: FC<AudioFileUploadProps> = ({
  file,
  status = 'idle',
  progress,
  uploadedBytes,
  totalBytes,
  secondsLeft,
  processing,
  error,
  disabled,
  className,
  onFileSelect,
  onValidationError,
  onRemove,
}) => {
  const errorId = useId()
  const pickIdRef = useRef(0)
  const [validationErrorCode, setValidationErrorCode] =
    useState<AudioFileErrorCode | null>(null)
  const [validating, setValidating] = useState(false)

  useEffect(
    () => () => {
      pickIdRef.current += 1
    },
    [],
  )

  const validationMessage = validationErrorCode
    ? AUDIO_FILE_ERROR_MESSAGES[validationErrorCode]
    : null
  const errorMessage = error || validationMessage

  const getDropzoneState = (): AudioFileDropzoneState => {
    if (errorMessage) {
      return 'error'
    }
    if (file) {
      return 'replace'
    }
    return 'enabled'
  }

  const handleFilePick = useCallback(
    async (pickedFile: File): Promise<void> => {
      const pickId = pickIdRef.current + 1
      pickIdRef.current = pickId
      setValidating(true)
      setValidationErrorCode(null)

      const code = await validateAudioFile(pickedFile)

      if (pickId !== pickIdRef.current) {
        return
      }

      setValidating(false)

      if (code) {
        setValidationErrorCode(code)
        onValidationError?.(code, pickedFile)
        return
      }

      onFileSelect(pickedFile)
    },
    [onFileSelect, onValidationError],
  )

  const handleRemove = (): void => {
    pickIdRef.current += 1
    setValidating(false)
    setValidationErrorCode(null)
    onRemove?.()
  }

  return (
    <Container className={className}>
      <DropzoneWrapper>
        <AudioFileDropzone
          state={getDropzoneState()}
          disabled={disabled}
          loading={validating}
          errorId={errorId}
          onFilePick={(pickedFile) => void handleFilePick(pickedFile)}
        />
        {Boolean(errorMessage) && (
          <ErrorMessage id={errorId} role='alert'>
            <ErrorIconWrapper>
              <ErrorIcon width={16} height={16} />
            </ErrorIconWrapper>
            <span>{errorMessage}</span>
          </ErrorMessage>
        )}
      </DropzoneWrapper>
      {Boolean(file) && (
        <TrackWrapper>
          <AudioFileUploadTrackItem
            name={file?.name || ''}
            size={file?.size}
            progress={progress}
            uploadedBytes={uploadedBytes}
            totalBytes={totalBytes}
            secondsLeft={secondsLeft}
            processing={processing}
            uploaded={status === 'uploaded'}
            onRemove={handleRemove}
          />
          {status === 'uploading' && (
            <KeepPageOpenNote>{KEEP_PAGE_OPEN_MESSAGE}</KeepPageOpenNote>
          )}
        </TrackWrapper>
      )}
    </Container>
  )
}
